/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Event } from '@msdyn365-commerce-modules/retail-actions';

import { ComparisonItem } from './base';

/**
 * Represents base functionality of a comparison manager which operates a list of items for comparison.
 */
export class ComparisonManager<ItemId, Item extends ComparisonItem<ItemId> = ComparisonItem<ItemId>> {
    public readonly listModifiedEvent: Event;

    public get lastUpdateDate(): Date {
        return this._lastUpdateDate;
    }

    private _lastUpdateDate: Date;

    private _comparisonItems: Item[];

    public get items(): Item[] {
        return this._comparisonItems;
    }

    public constructor(...items: Item[]) {
        this._comparisonItems = items;
        this.listModifiedEvent = new Event();
        this._lastUpdateDate = new Date(Date.now());
    }

    public setItems(items: Item[]): void {
        this._comparisonItems = items;
        this._update();
    }

    public addItem(item: Item): void {
        this._comparisonItems.push(item);
        this._update();
    }

    public clearAll(): void {
        this.setItems([]);
    }

    public removeItem(item: Item): void {
        this._comparisonItems = this._comparisonItems.filter(currentItem => currentItem !== item);
        this._update();
    }

    public removeItemById(itemId: ItemId): void {
        this._comparisonItems = this._comparisonItems.filter(currentItem => currentItem.itemId !== itemId);
        this._update();
    }

    public hasItem(item: Item): boolean {
        return this.items.includes(item);
    }

    public hasItemById(itemId: ItemId): boolean {
        return this.items.some(item => item.itemId === itemId);
    }

    private _update(): void {
        this._lastUpdateDate = new Date(Date.now());
        this.listModifiedEvent.trigger();
    }
}
